import React, { useState, useRef } from "react";
import { Camera } from "react-camera-pro";
import { FaCamera, FaTimes, FaSync } from "react-icons/fa";

const CameraComponent = ({ onCapture }) => {
  const camera = useRef(null);
  const [numberOfCameras, setNumberOfCameras] = useState(0);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);

  const TARGET_PHOTO_BYTES = 350 * 1024;
  const SIZE_TOLERANCE_BYTES = 15 * 1024;
  const MIN_JPEG_QUALITY = 0.3;

  const loadImage = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Captured photo load failed"));
      img.src = dataUrl;
    });

  const canvasFromImage = (img) => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas context unavailable");
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  };

  const canvasToBlob = (canvas, quality) =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas toBlob failed"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        quality
      );
    });

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Blob conversion failed"));
      reader.readAsDataURL(blob);
    });

  // Binary-search JPEG quality to keep the blob near the requested size without upscaling.
  const compressCanvasToTarget = async (canvas) => {
    let bestBlob = await canvasToBlob(canvas, 1);
    const target = TARGET_PHOTO_BYTES;
    const tolerance = SIZE_TOLERANCE_BYTES;

    if (bestBlob.size <= target + tolerance && bestBlob.size >= target - tolerance) {
      return bestBlob;
    }

    if (bestBlob.size < target - tolerance) {
      return bestBlob;
    }

    let low = MIN_JPEG_QUALITY;
    let high = 1;
    for (let i = 0; i < 7; i++) {
      const quality = (low + high) / 2;
      if (Math.abs(high - low) < 0.01) {
        break;
      }
      const blob = await canvasToBlob(canvas, quality);
      if (Math.abs(blob.size - target) < Math.abs(bestBlob.size - target)) {
        bestBlob = blob;
      }

      if (blob.size > target + tolerance) {
        high = quality;
      } else if (blob.size < target - tolerance) {
        low = Math.max(quality, MIN_JPEG_QUALITY);
      } else {
        return blob;
      }
    }

    const minBlob = await canvasToBlob(canvas, MIN_JPEG_QUALITY);
    if (
      Math.abs(minBlob.size - target) < Math.abs(bestBlob.size - target) ||
      (bestBlob.size > target + tolerance && minBlob.size < bestBlob.size)
    ) {
      bestBlob = minBlob;
    }

    return bestBlob;
  };

  const preparePhoto = async (dataUrl) => {
    if (typeof dataUrl !== "string") {
      return null;
    }

    try {
      const img = await loadImage(dataUrl);
      const canvas = canvasFromImage(img);
      const blob = await compressCanvasToTarget(canvas);
      return blobToDataUrl(blob);
    } catch (error) {
      console.error("Failed to process photo", error);
      return null;
    }
  };

  const handleCapturePhoto = async () => {
    if (!camera.current) return;
    setIsProcessingPhoto(true);
    try {
      const base64Photo = camera.current.takePhoto();
      const processed = await preparePhoto(base64Photo);
      setCapturedPhoto(processed || base64Photo);
    } catch (error) {
      console.error("Photo capture failed", error);
    } finally {
      setIsProcessingPhoto(false);
    }
  };

  const handleKeepPhoto = () => {
    if (!capturedPhoto) return;
    onCapture(capturedPhoto);
    setShowCamera(false);
    setCapturedPhoto(null);
  };

  const handleRetakePhoto = () => {
    setCapturedPhoto(null);
    setIsProcessingPhoto(false);
  };

  const handleSwitchCamera = () => {
    if (camera.current) {
      camera.current.switchCamera();
    }
  };

  const closeCamera = () => {
    setShowCamera(false);
    setCapturedPhoto(null);
  };

  return (
    <div>
      {!showCamera && (
        <button
          onClick={() => setShowCamera(true)}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover flex items-center justify-center"
        >
          <FaCamera className="w-6 h-6" />
        </button>
      )}
      {showCamera && (
        <div style={{ width: '100vw', height: '100vh', margin: 'auto', position: 'fixed', top: '0', left: '0', right: '0', bottom: '0', zIndex: '1000', backgroundColor: 'rgba(0, 0, 0, 0.8)' }}>
          {!capturedPhoto ? (
            <>
              <Camera 
                ref={camera} 
                facingMode="environment"
                numberOfCamerasCallback={setNumberOfCameras} 
              />
              <button
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
                onClick={handleCapturePhoto}
                disabled={isProcessingPhoto}
                title={isProcessingPhoto ? "Pildi töötlemine" : undefined}
                style={{
                  position: 'absolute',
                  bottom: '35%',
                  right: '2%',
                  opacity: isProcessingPhoto ? 0.6 : 1,
                  cursor: isProcessingPhoto ? 'wait' : 'pointer'
                }}
              >
                <FaCamera className="w-6 h-6" />
              </button>
              <button
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
                style={{ position: 'absolute', bottom: '10%', left: '2%' }}
                hidden={numberOfCameras <= 1}
                onClick={handleSwitchCamera}
              >
                <FaSync className="w-6 h-6" />
              </button>
              <button
                className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
                style={{ position: 'absolute', top: '10%', right: '2%' }}
                onClick={closeCamera}
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: 'white' }}>
              <img src={capturedPhoto} alt="Captured" style={{ width: '90vw', maxHeight: '75vh', marginLeft: "5%" }} />
              <div style={{ marginTop: '20px'}}>
                <button
                  className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
                  onClick={handleRetakePhoto}
                  style={{ marginRight: '10px', bottom: '10%'}}
                >
                  Tee uus pilt
                </button>
                <button
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
                  onClick={handleKeepPhoto}
                >
                  Salvesta pilt
                </button>
                
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CameraComponent;
